import type { ChargePoint, Connector, SiteDetail, SiteStatus, Station } from '@ev/shared'
import { useStrings } from '../i18n'
import { formatPower } from './format'
import { withKeys } from './keys'
import { StatusBadge } from './StatusBadge'
import { useLocale } from './useLocale'
import { VoltageBadge, VoltageConflictNote, VoltageProvenanceNote } from './voltage/VoltageBadge'

/**
 * The station → point → connector breakdown, which is the shape EIPA actually
 * publishes: a site is a pool of stations, a station holds charge points, and a
 * point exposes one or more physical connectors.
 *
 * Flattening it to "this site has 6 connectors" would lose the thing a driver
 * needs, which is that arriving at the site does not mean arriving at a free
 * connector of the right kind.
 */

const connectorIdentity = (connector: Connector): string =>
  `${connector.interfaces.join('-')}-${connector.powerKw}-${connector.cableAttached}`

const ConnectorRow = ({
  connector,
  position,
  total,
}: {
  connector: Connector
  position: number
  total: number
}) => {
  const strings = useStrings()
  const locale = useLocale()

  return (
    <li className="ev-connector">
      <div className="ev-connector__head">
        <span className="ev-connector__standards">
          {connector.interfaces.map((standard) => strings.connector[standard]).join(' / ')}
        </span>
        <span className="visually-hidden">{strings.detail.connectorHeading(position, total)}</span>
        <span className="ev-connector__power">
          {formatPower(connector.powerKw, locale)}
          {/* The registry's figure stands; the marker says another source does
              not agree with it, and the block below prints both. */}
          {connector.disputedPower !== undefined && (
            <>
              <span aria-hidden="true" className="ev-volt__dispute">
                ≠
              </span>
              <span className="visually-hidden">{`, ${strings.voltage.conflictTitle}`}</span>
            </>
          )}
        </span>
      </div>

      <dl className="ev-connector__facts">
        <dt>{strings.detail.cableAttached}</dt>
        <dd>
          {connector.cableAttached
            ? strings.detail.cableAttachedYes
            : strings.detail.cableAttachedNo}
        </dd>

        <dt>{strings.voltage.heading}</dt>
        <dd>
          <VoltageBadge voltage={connector.voltage} />
        </dd>
      </dl>

      <VoltageProvenanceNote voltage={connector.voltage} />
      <VoltageConflictNote connector={connector} />
    </li>
  )
}

const PointCard = ({ point, status }: { point: ChargePoint; status: SiteStatus | undefined }) => {
  const strings = useStrings()
  const locale = useLocale()
  const connectors = withKeys(point.connectors, connectorIdentity)

  return (
    <li className="ev-point">
      <div className="ev-point__head">
        <div>
          <p className="ev-point__power">{formatPower(point.maxPowerKw, locale)}</p>
          <p className="ev-point__current">{strings.currentType[point.currentType]}</p>
        </div>
        <StatusBadge status={status?.points[point.id]} />
      </div>

      <dl className="ev-point__facts">
        <dt>{strings.detail.pointCode}</dt>
        <dd>
          <code>{point.code}</code>
        </dd>
        {point.chargingModes.length > 0 && (
          <>
            <dt>{strings.detail.chargingModes}</dt>
            <dd>{point.chargingModes.join(', ')}</dd>
          </>
        )}
      </dl>

      {connectors.length === 0 ? (
        <p className="ev-empty ev-empty--inline">{strings.detail.noConnectors}</p>
      ) : (
        <ul className="ev-connector-list">
          {connectors.map((entry) => (
            <ConnectorRow
              key={`${point.id}-${entry.key}`}
              connector={entry.item}
              position={entry.position}
              total={connectors.length}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

const StationCard = ({
  station,
  position,
  total,
  status,
}: {
  station: Station
  position: number
  total: number
  status: SiteStatus | undefined
}) => {
  const strings = useStrings()

  return (
    <section className="ev-station" aria-label={strings.detail.stationHeading(position, total)}>
      <header className="ev-station__head">
        <h4 className="ev-station__title">{strings.detail.stationHeading(position, total)}</h4>
        <span className="ev-station__type">{strings.stationType[station.type]}</span>
      </header>

      <dl className="ev-station__facts">
        {station.authMethods.length > 0 && (
          <>
            <dt>{strings.detail.authMethods}</dt>
            <dd>{station.authMethods.map((method) => strings.authMethod[method]).join(', ')}</dd>
          </>
        )}
        {station.paymentMethods.length > 0 && (
          <>
            <dt>{strings.detail.paymentMethods}</dt>
            <dd>
              {station.paymentMethods.map((method) => strings.paymentMethod[method]).join(', ')}
            </dd>
          </>
        )}
      </dl>

      {station.points.length === 0 ? (
        <p className="ev-empty ev-empty--inline">{strings.detail.noPoints}</p>
      ) : (
        <ul className="ev-point-list">
          {station.points.map((point) => (
            <PointCard key={point.id} point={point} status={status} />
          ))}
        </ul>
      )}
    </section>
  )
}

export const StationBreakdown = ({
  site,
  status,
}: {
  site: SiteDetail
  status: SiteStatus | undefined
}) => {
  const stations = withKeys(site.stations, (station) => station.id)

  return (
    <div className="ev-stations">
      {stations.map((entry) => (
        <StationCard
          key={entry.key}
          station={entry.item}
          position={entry.position}
          total={stations.length}
          status={status}
        />
      ))}
    </div>
  )
}
